import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { MessageSquareHeart, ShieldCheck } from "lucide-react";
import { useFeedback } from "@/context/FeedbackContext";
import { SiteFooter } from "@/components/site/SiteFooter";
import { FeedOutageBanner } from "@/components/FeedOutageBanner";
import { fcmLocalToken, onFcmMessage } from "@/lib/fcm";
import {
  LayoutDashboard, Wallet, MessageSquare, Newspaper, Bell, Settings,
  LogOut, Search, AlertTriangle, BellRing, GraduationCap, Radar,
  FlaskConical, NotebookPen, Coins, ArrowLeftRight, Gift,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useBilling } from "@/context/BillingContext";
import { UpgradeButton, PlanBadge } from "@/components/Locked";
import { isAndroidApp } from "@/lib/platform";
import { api, fmtUsd } from "@/lib/api";
import { SearchDialog } from "@/components/SearchDialog";
import { NotificationsPanel } from "@/components/NotificationsPanel";
import { MobileNav } from "@/components/MobileNav";
import { Hint } from "@/components/Hint";
import { announceAlert, unlockAudio } from "@/lib/attention";
import { listenToServiceWorker } from "@/lib/push";
import { ModeSwitch } from "@/components/Beginner";
import { ExplainPage } from "@/components/ExplainPage";
import { Tour } from "@/components/Tour";
import { PageIntro } from "@/components/PageIntro";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, tid: "nav-dashboard", k: "nav_dashboard" },
  { to: "/opportunities", label: "Opportunities", icon: Radar, tid: "nav-opportunities", k: "scanner" },
  { to: "/portfolio", label: "Portfolio", icon: Wallet, tid: "nav-portfolio", k: "nav_portfolio" },
  { to: "/chat", label: "AI Research", icon: MessageSquare, tid: "nav-chat", k: "nav_chat" },
  { to: "/news", label: "News", icon: Newspaper, tid: "nav-news", k: "nav_news" },
  { to: "/performance", label: "Performance & Transparency", icon: ShieldCheck, tid: "nav-performance", k: "nav_performance" },
  { to: "/markets", label: "Live Markets", icon: Coins, tid: "nav-coinbase", k: "nav_coinbase" },
  { to: "/trade", label: "Paper Trading", icon: ArrowLeftRight, tid: "nav-trade", k: "nav_trade" },
  { to: "/strategy-lab", label: "Strategy Lab", icon: FlaskConical, tid: "nav-lab", k: "nav_lab" },
  { to: "/alerts", label: "Alerts", icon: Bell, tid: "nav-alerts", k: "nav_alerts" },
  { to: "/journal", label: "Journal", icon: NotebookPen, tid: "nav-journal", k: "nav_journal" },
  { to: "/learn", label: "Learn Crypto", icon: GraduationCap, tid: "nav-learn", k: "nav_learn" },
  { to: "/settings", label: "Settings", icon: Settings, tid: "nav-settings", k: "nav_settings" },
];

const Disclaimer = () => (
  <div
    data-testid="disclaimer-banner"
    className="w-full flex items-center justify-center gap-2 px-4 py-2 text-[11px] md:text-xs"
    style={{ background: "rgba(245,158,11,0.08)", borderBottom: "1px solid rgba(245,158,11,0.25)", color: "#fcd34d" }}
  >
    <AlertTriangle className="w-3.5 h-3.5" />
    <span className="font-medium">PAPER TRADING MODE</span>
    <span className="opacity-80 hidden sm:inline">— Simulated trades only. No real money. Not financial advice. Do your own research.</span>
  </div>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const { openFeedback } = useFeedback();
  const { plan } = useBilling();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const compactChrome = pathname === "/chat"; // AI Research opens expanded: smaller brand bar, no intro card — the nav stays
  useEffect(() => { // Firebase push while the tab is open → in-app toast (background pushes are rendered by /sw.js)
    let off = () => {};
    if (fcmLocalToken()) onFcmMessage((m) => toast(m.title || "EV-IDEX alert", { description: m.body, action: m.url ? { label: "Open", onClick: () => navigate(m.url) } : undefined })).then((u) => { off = u; }).catch(() => {});
    return () => off();
  }, [navigate]);
  useEffect(() => {
    const h = (ev) => {
      const d = ev.detail || {};
      toast.warning(d.message || "Upgrade required", { description: d.code === "limit_reached" ? (isAndroidApp() ? "Daily limit reached on your current plan." : "Upgrade your plan for higher daily limits.") : `This feature is part of the ${d.required_plan === "premium" ? "Premium" : "Pro"} plan.`,
        action: isAndroidApp() ? undefined : { label: "View plans", onClick: () => navigate(`/pricing?plan=${d.required_plan || "pro"}`) }, id: "upgrade-required" });
    };
    window.addEventListener("apx:upgrade-required", h);
    return () => window.removeEventListener("apx:upgrade-required", h);
  }, [navigate]);
  const [cash, setCash] = useState(100000);
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [bellFlash, setBellFlash] = useState(false);
  const lastSeen = useRef(null);

  const loadNotifCount = () => {
    api.get("/notifications").then((r) => {
      const n = r.data.unread || 0;
      // New unread arrived since last poll (e.g. from the 15-min background sweep) → chime + flash
      if (lastSeen.current !== null && n > lastSeen.current) {
        const latest = (r.data.notifications || []).find((x) => !x.read);
        announceAlert(latest?.title || "New EV-IDEX alert", latest?.body || "");
      }
      lastSeen.current = n;
      setUnread(n);
    }).catch((e) => console.warn("[apex] request failed:", e?.message || e));
  };

  useEffect(() => {
    api.get("/portfolio").then((r) => setCash(r.data.total_value)).catch((e) => console.warn("[apex] request failed:", e?.message || e));
    loadNotifCount();
    const i = setInterval(() => {
      api.get("/portfolio").then((r) => setCash(r.data.total_value)).catch((e) => console.warn("[apex] request failed:", e?.message || e));
      loadNotifCount();
    }, 20000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    window.addEventListener("apx:notifications-changed", loadNotifCount);
    const onFired = () => { setBellFlash(true); setTimeout(() => setBellFlash(false), 4000); };
    window.addEventListener("apx:alert-fired", onFired);
    // Browsers only allow audio after a user gesture — unlock on the first interaction
    const unlock = () => { unlockAudio(); window.removeEventListener("pointerdown", unlock); window.removeEventListener("keydown", unlock); };
    window.addEventListener("pointerdown", unlock); window.addEventListener("keydown", unlock);
    const stopSw = listenToServiceWorker(navigate);
    return () => {
      stopSw();
      window.removeEventListener("apx:notifications-changed", loadNotifCount);
      window.removeEventListener("apx:alert-fired", onFired);
      window.removeEventListener("pointerdown", unlock); window.removeEventListener("keydown", unlock);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setSearchOpen(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="App grain min-h-screen flex flex-col" data-testid="app-shell">
      <Disclaimer />
      <FeedOutageBanner />

      {/* Top bar */}
      <header
        data-testid="app-header"
        className="sticky top-0 z-40 apx-glass flex items-center gap-3 md:gap-5 px-3 md:px-6 py-2"
      >
        <button
          data-testid="brand-logo"
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 shrink-0"
          aria-label="EV-IDEX — Dashboard"
        >
          <img
            src="/brand/evidex-logo-notag.jpg"
            alt="EV-IDEX"
            className="h-[60px] w-auto rounded-lg md:hidden"
            data-testid="brand-logo-mobile"
          />
          <img
            src="/brand/evidex-logo-notag.jpg"
            alt="EV-IDEX"
            className={`${compactChrome ? "h-[56px]" : "h-[84px] xl:h-[112px]"} w-auto rounded-lg hidden md:block`}
            data-testid="brand-logo-desktop"
          />
          <span className="text-[10px] font-mono-ticker px-1.5 py-0.5 rounded border border-cyan-900/60 text-cyan-300/80 hidden lg:inline">v0.1</span>
        </button>
        <div className="hidden sm:block"><ModeSwitch /></div>
        <ExplainPage />

        <Hint k="search" side="bottom" asChild>
        <button
          data-testid="open-search-btn"
          onClick={() => setSearchOpen(true)}
          className="hidden lg:flex items-center gap-2 flex-1 min-w-0 max-w-xl text-left apx-ring"
          style={{ background: "var(--bg-1)", border: "1px solid var(--border-1)", borderRadius: 10, padding: "8px 12px" }}
        >
          <Search className="w-4 h-4 text-slate-400 shrink-0" />
          <span className="text-sm text-slate-400 truncate">Search any crypto…</span>
          <span className="ml-auto hidden xl:inline text-[10px] font-mono-ticker text-slate-500 border border-slate-700 rounded px-1.5 py-0.5">⌘K</span>
        </button>
        </Hint>

        <button
          data-testid="search-icon-mobile"
          onClick={() => setSearchOpen(true)}
          className="lg:hidden btn-ghost rounded-lg p-2"
          aria-label="Search"
        >
          <Search className="w-5 h-5" />
        </button>

        <div className="ml-auto flex items-center gap-1.5 lg:gap-3 shrink-0">
          {plan === "free" ? (
            <UpgradeButton plan="pro" testid="header-upgrade-btn" label="Upgrade" className="hidden sm:inline-flex" />
          ) : (
            <span className="hidden sm:inline-flex"><PlanBadge plan={plan} /></span>
          )}
          <Hint k="portfolio_value" side="bottom" className="text-right hidden xl:block cursor-help">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-ticker">Portfolio</div>
            <div data-testid="header-portfolio-value" className="font-mono-ticker text-sm font-semibold text-emerald-400">
              {cash !== null ? fmtUsd(cash) : "—"}
            </div>
          </Hint>
          <Hint k="notifications" side="bottom" asChild>
          <button
            data-testid="notif-bell-btn"
            onClick={() => { setNotifOpen(true); }}
            className={`btn-ghost rounded-lg p-2 relative ${bellFlash ? "apx-bell-flash" : ""}`}
            aria-label="Notifications"
            data-flashing={bellFlash ? "true" : "false"}
          >
            <BellRing className={`w-4 h-4 ${bellFlash ? "apx-bell-swing text-amber-300" : ""}`} />
            {unread > 0 && (
              <span
                data-testid="notif-unread-dot"
                className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-[9px] text-white font-mono-ticker font-bold flex items-center justify-center"
              >
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </button>
          </Hint>
          <button
            data-testid="nav-feedback-btn"
            onClick={() => openFeedback({ source: "nav" })}
            className="btn-ghost rounded-lg p-2 md:px-3 md:py-2 flex items-center gap-1.5 text-sm"
            aria-label="Give Feedback"
            title="Give Feedback"
          >
            <MessageSquareHeart className="w-4 h-4 text-cyan-300" />
            <span className="hidden xl:inline">Feedback</span>
          </button>
          <Hint k="logout" side="bottom" asChild>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="btn-ghost rounded-lg p-2"
            aria-label="Logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
          </Hint>
        </div>
      </header>

      <div className="flex-1 flex relative">
        {/* Desktop sidebar */}
        <aside className="hidden md:flex flex-col w-56 shrink-0 border-r"
               style={{ borderColor: "var(--border-1)", background: "rgba(15,20,28,0.5)" }}>
          <nav className="p-3 flex flex-col gap-1 sticky top-[76px]">
            {NAV.map(({ to, label, icon: Icon, tid, k }) => (
              <Hint key={to} k={k} side="right" className="block">
              <NavLink
                to={to} data-testid={tid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    isActive
                      ? "bg-slate-800/70 text-cyan-300 border border-cyan-900/50"
                      : "text-slate-300 hover:bg-slate-800/40"
                  }`
                }
              >
                <Icon className="w-4 h-4" /> {label}
              </NavLink>
              </Hint>
            ))}
            {user?.is_admin && (
              <NavLink to="/owner" data-testid="nav-owner-tools"
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-amber-200/90 hover:bg-slate-800/40 border border-amber-900/40 mt-1">
                <Gift className="w-4 h-4" /> Owner tools
              </NavLink>
            )}
            <div className="mt-6 px-3 py-3 rounded-lg apx-card">
              <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500 mb-1">Signed in</div>
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
          </nav>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0 pb-[calc(5.5rem+env(safe-area-inset-bottom))] md:pb-6 relative z-10">
          {!compactChrome && <PageIntro />}
          <Outlet />
          <Tour />
          <SiteFooter compact />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <MobileNav user={user} plan={plan} onLogout={logout} />

      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
      <NotificationsPanel open={notifOpen} onOpenChange={setNotifOpen} onChanged={loadNotifCount} />
    </div>
  );
}
